// @fluxora/auth — public surface.
//
// Two providers feed ONE shared AuthContext:
//   - <AuthProvider config={...}>      real OIDC (Auth Code + PKCE, Keycloak)
//   - <DummyAuthProvider users org>    dev-only in-memory auth
//
// Consumers (`useAuth`, `RequireAuth`, `RequireRole`) are provider-agnostic.
// Storage contract: access token in memory; refresh token in httpOnly cookie.
// JWT-authoritative: tenant/country/roles come only from verified claims.
//
// CODE-SPLIT: the real OIDC provider (`./oidc.js`) statically imports
// `oidc-client-ts`. To keep the dev path (DummyAuthProvider) 100% free of that
// dependency, `AuthProvider` is exported as a React.lazy wrapper — its module
// (and `oidc-client-ts`) is only fetched/evaluated when <AuthProvider> first
// renders. Importing this entry for the dummy provider / hooks / types does NOT
// pull in oidc-client-ts at module-load. Consumers of <AuthProvider> must wrap
// it in <Suspense> (see US App.tsx). Type-only exports are erased at runtime.

import { lazy } from 'react';

// --- Types ---
export type {
  AuthUser,
  AuthContextValue,
  DummyUser,
  DummyOrganization,
  DummyUserRole,
  DummyAuthState,
  DummyAuthContextValue,
  OidcConfig,
} from './types.js';

// --- Shared context + guards (provider-agnostic) ---
export {
  AuthContext,
  useAuth,
  useIsAuthenticated,
  useIsAuthHydrated,
  RequireAuth,
  RequireRole,
} from './context.js';
export type { RequireAuthProps, RequireRoleProps } from './context.js';

// --- Real OIDC provider (lazy: only loads oidc-client-ts on first render) ---
export const AuthProvider = lazy(() =>
  import('./oidc.js').then((m) => ({ default: m.AuthProvider })),
);
export type { AuthProviderProps } from './oidc.js';

// --- ROPC (custom-form) provider: SPA form → backend /v1/auth/login → KC
//     password grant. No redirect, no oidc-client-ts. ---
export { RopcAuthProvider, readSessionEndReason } from './ropc.js';
export { canSealPassword, isLoginChallenge, sealPassword } from './login-crypto.js';
export type { LoginChallenge, SealedLoginBody } from './login-crypto.js';
export type { RopcAuthProviderProps, RopcConfig, SessionEndReason } from './ropc.js';

// --- Dummy (dev-only) provider + selector hooks ---
export {
  DummyAuthProvider,
  useDummyAuth,
  useAuthToken,
  useTenantId,
  useActorUserId,
  useCurrentUser,
  useUserRole,
  __dummyAuthTestHooks,
} from './dummy.js';
export type { DummyAuthProviderProps, DummyAuthSeed } from './dummy.js';
